import {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
  type ChangeEvent, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
} from 'react';
import type { PeopleSearchResponse } from '../../shared/api-types.js';
import { api, failureKind, queryString, type ApiResult } from '../api.js';
import { Button, IconButton } from './Button.js';
import { pluralise } from './format.js';
import { Icon } from './Icon.js';
import { Spinner } from './Spinner.js';
import { inlineFailureText } from './StateView.js';

// The searchable picker (03-components §3.4): an input the reader types into to find one entry in a
// set too large to hold up front — a person — and then chooses it. The mechanism is the contract:
// `role="combobox"` on the input with `aria-autocomplete="list"`, `aria-expanded`, `aria-controls`
// naming the `role="listbox"` and `aria-activedescendant` naming the active `role="option"`; focus
// stays in the input throughout (options take no focus — pointer-down on the list is prevented). Keys:
// ↓/↑ open and move, Home/End, Enter chooses and closes, Escape closes and restores the held choice's
// text, Tab closes without choosing; handlers are IME-safe. The query goes on a 300 ms pause, never
// below two characters and never for the empty string; a late answer never replaces a later query's
// (an incrementing request id; the previous request is aborted). One visible `role="status"` line
// names every state, and says so when the answer was capped; between a keystroke at or past the
// minimum and its answer it is empty, or keeps the previous answer's count — never the below-minimum
// sentence, which would call a query under way too short (`nextStatusLine`). Typed text is never a
// choice: leaving the field restores the held choice's name. The held choice shows by its name, never its id.

export interface ComboboxOption {
  id: string;
  /** The display name — what the field shows when this is held. */
  label: string;
  /** Shown beneath the label only when the server sent it: what tells two identical names apart (an email on a collision). */
  secondary?: string;
}

export interface ComboboxSearchResult<T extends ComboboxOption = ComboboxOption> {
  items: T[];
  /** The server's cap: when `items.length >= limit` the answer may be cut and the status line says so. */
  limit?: number;
}

/** The words of one set's combobox; `PEOPLE_WORDS` is the people search's. */
export interface ComboboxWords {
  /** Below the minimum length — so the first keystroke never reads as an empty result; a function of the minimum, so the sentence is true for any `minLength`. */
  belowMinimum: (minLength: number) => string;
  /** Loading, after ~200 ms. */
  searching: string;
  count: (count: number) => string;
  /** The answer was capped at the server: the line must say so (§3.4). */
  capped: (count: number, limit: number) => string;
  /** No entry matches what was typed, naming it. */
  none: (query: string) => string;
  /** 403: the reader may not search this set — never an empty list. */
  denied: string;
  /** Any other failure; the popup offers a retry. */
  failed: string;
  /** A choice is held and the field shows its name. */
  chosen: (label: string) => string;
  retry: string;
  /** The Clear control's accessible name. */
  clear: string;
}

export type ComboboxStatus =
  | { kind: 'held'; label: string }
  | { kind: 'short'; minLength: number }
  | { kind: 'searching' }
  | { kind: 'results'; count: number; limit: number | null; query: string }
  | { kind: 'denied' }
  | { kind: 'error'; message: string };

/** The one status line's text for a state (§3.4: every state named, the capped answer said). */
export function comboboxStatusLine(status: ComboboxStatus, words: ComboboxWords): string {
  switch (status.kind) {
    case 'held':
      return words.chosen(status.label);
    case 'short':
      return words.belowMinimum(status.minLength);
    case 'searching':
      return words.searching;
    case 'results':
      if (status.count === 0) return words.none(status.query);
      if (status.limit !== null && status.count >= status.limit) return words.capped(status.count, status.limit);
      return words.count(status.count);
    case 'denied':
      return words.denied;
    case 'error':
      return words.failed;
  }
}

/** What the field shows after the reader leaves it: the held choice's name, or nothing — never typed text (§3.4). */
export function textAfterLeaving(value: ComboboxOption | null): string {
  return value?.label ?? '';
}

/** The search's phase: nothing asked, a request running, an answer, or one of the two refusals. */
export type ComboboxPhase<T extends ComboboxOption = ComboboxOption> =
  | { kind: 'idle' }
  | { kind: 'loading'; query: string }
  | { kind: 'ready'; query: string; items: T[]; limit: number | null }
  | { kind: 'denied'; query: string }
  | { kind: 'error'; query: string; message: string };

/**
 * The status the line names right now (pure, pinned under node), or null when nothing applies: a request not yet
 * shown as loading, or a pause after a keystroke at or past the minimum — then the line is `nextStatusLine`'s.
 */
export function comboboxStatusFor<T extends ComboboxOption>(input: { phase: ComboboxPhase<T>; loadingShown: boolean; value: ComboboxOption | null; text: string; minLength: number }): ComboboxStatus | null {
  const { phase, loadingShown, value, text, minLength } = input;
  if (phase.kind === 'loading') return loadingShown ? { kind: 'searching' } : null;
  if (phase.kind === 'ready') return { kind: 'results', count: phase.items.length, limit: phase.limit, query: phase.query };
  if (phase.kind === 'denied') return { kind: 'denied' };
  if (phase.kind === 'error') return { kind: 'error', message: phase.message };
  if (value && text === value.label) return { kind: 'held', label: value.label };
  if (text.trim().length < minLength) return { kind: 'short', minLength };
  return null;
}

/**
 * The line's words for a render, and what the next render may carry (pure, pinned under node). With no status — the
 * pause between a keystroke at or past the minimum and its answer, or a request not yet shown as loading — the line
 * keeps the previous ANSWER's count rather than flickering, and otherwise stands empty. Why only an answer carries:
 * the below-minimum sentence carried over once, so the second character still read "Type two or more characters"
 * while its query was already on its way; a held name, a refusal or an error are over the moment the text changes.
 * "Searching…" neither carries nor clears: the count it interrupted comes back behind it until the answer lands.
 */
export function nextStatusLine(status: ComboboxStatus | null, words: ComboboxWords, carried: string): { text: string; carried: string } {
  if (status === null) return { text: carried, carried };
  const text = comboboxStatusLine(status, words);
  if (status.kind === 'results') return { text, carried: text };
  if (status.kind === 'searching') return { text, carried };
  return { text, carried: '' };
}

export interface ComboboxProps<T extends ComboboxOption = ComboboxOption> {
  /** Visible above the input, always (§3.4 anatomy). */
  label: ReactNode;
  /** The held choice; the parent owns it. */
  value: T | null;
  onChange: (next: T | null) => void;
  /** Runs one query; the signal aborts it when a later query supersedes it or the field is left. */
  search: (query: string, signal: AbortSignal) => Promise<ApiResult<ComboboxSearchResult<T>>>;
  words: ComboboxWords;
  /** Characters before a query is sent (the people route's `min(2)`). */
  minLength?: number;
  /** The pause in typing before the query goes. */
  debounceMs?: number;
  /** How long a request runs before the loading state is shown. */
  loadingAfterMs?: number;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** An example, never the label. */
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  className?: string;
  /** Offer the Clear control while a choice is held (an optional field; default true). */
  clearable?: boolean;
}

type Phase<T extends ComboboxOption> = ComboboxPhase<T>;

interface PopupPosition {
  top: number | null;
  bottom: number | null;
  left: number;
  width: number;
  maxHeight: number;
}

const POPUP_GAP = 4;
const POPUP_MARGIN = 8;
const POPUP_MIN_HEIGHT = 160;
const POPUP_MAX_HEIGHT = 320;

/** The edges the popup is placed against, in viewport coordinates. */
export interface PopupAnchor {
  /** The input's box: the popup takes its left edge and width, and flips above its top. */
  input: { top: number; bottom: number; left: number; width: number };
  /**
   * The bottom of the status line beneath the input. Why the popup hangs from here and not from the input: the
   * one visible `role="status"` line carries the count and the capped notice (§3.4: "both what is seen and what is
   * announced"), and those two states exist only while the list is open — anchored to the input, the list sat
   * exactly over the line and a capped answer read as complete.
   */
  statusBottom: number;
  /** The visual viewport (`window.visualViewport`): what the on-screen keyboard leaves. */
  viewTop: number;
  viewBottom: number;
  /** `window.innerHeight`, for a flipped popup's `bottom` offset. */
  innerHeight: number;
}

/** Where the popup goes (pure, so the geometry is pinned under node): beneath the status line, or flipped above the input. */
export function popupPlacement(anchor: PopupAnchor): PopupPosition {
  const { input, statusBottom, viewTop, viewBottom, innerHeight } = anchor;
  const hangFrom = Math.max(input.bottom, statusBottom);
  const below = viewBottom - hangFrom - POPUP_GAP - POPUP_MARGIN;
  const above = input.top - viewTop - POPUP_GAP - POPUP_MARGIN;
  const flip = below < POPUP_MIN_HEIGHT && above > below;
  const maxHeight = Math.max(96, Math.min(flip ? above : below, POPUP_MAX_HEIGHT));
  return {
    top: flip ? null : hangFrom + POPUP_GAP,
    bottom: flip ? innerHeight - input.top + POPUP_GAP : null,
    left: input.left,
    width: input.width,
    maxHeight,
  };
}

/**
 * The option ↓/↑ moves to (pure, pinned under node). With none active — the state right after an answer lands — ↓
 * reaches the first and ↑ the last, as the WAI-ARIA combobox pattern has it; otherwise the keys step and wrap.
 */
export function nextActiveIndex(current: number, count: number, down: boolean): number {
  if (count <= 0) return -1;
  if (current < 0) return down ? 0 : count - 1;
  return down ? (current + 1) % count : (current - 1 + count) % count;
}

export function Combobox<T extends ComboboxOption>({
  label, value, onChange, search, words, minLength = 2, debounceMs = 300, loadingAfterMs = 200, hint, error, required, placeholder, disabled, autoFocus, id: givenId, className, clearable = true,
}: ComboboxProps<T>) {
  const autoId = useId();
  const id = givenId ?? autoId;
  const inputId = `${id}-input`;
  const labelId = `${id}-label`;
  const listId = `${id}-listbox`;
  const statusId = `${id}-status`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const optionId = (index: number): string => `${id}-option-${index}`;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const statusRef = useRef<HTMLParagraphElement | null>(null);
  const [text, setText] = useState(value?.label ?? '');
  const [phase, setPhase] = useState<Phase<T>>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loadingShown, setLoadingShown] = useState(false);
  const [position, setPosition] = useState<PopupPosition | null>(null);
  const requestId = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const debounce = useRef<number | null>(null);
  const composing = useRef(false);
  // The previous answer's count, carried across the pause after a keystroke (`nextStatusLine`).
  const carriedStatus = useRef('');

  // The field shows the held choice by its name — also when the parent changes it from outside.
  const valueId = value?.id ?? null;
  const valueLabel = value?.label ?? '';
  useEffect(() => {
    setText(valueLabel);
  }, [valueId, valueLabel]);

  // Why one place: every path that ends a search (a new query, a choice, Escape, Tab, blur) must drop
  // the pending pause AND the request in flight AND make any answer still travelling stale.
  const cancelPending = useCallback(() => {
    if (debounce.current !== null) {
      window.clearTimeout(debounce.current);
      debounce.current = null;
    }
    controller.current?.abort();
    controller.current = null;
    requestId.current += 1;
  }, []);

  const run = useCallback((query: string) => {
    cancelPending();
    const mine = requestId.current;
    const abort = new AbortController();
    controller.current = abort;
    setPhase({ kind: 'loading', query });
    setOpen(true);
    void search(query, abort.signal).then((result) => {
      // A later query owns the field now: this answer is stale whatever it says (§3.4).
      if (mine !== requestId.current) return;
      controller.current = null;
      if (result.ok) {
        setPhase({ kind: 'ready', query, items: result.data.items, limit: result.data.limit ?? null });
        setActive(-1);
        return;
      }
      if (result.aborted) return;
      if (failureKind(result) === 'denied') setPhase({ kind: 'denied', query });
      // Why `inlineFailureText` (ruling Q11): the line under the picker says a failure as every other control's line does.
      else setPhase({ kind: 'error', query, message: inlineFailureText(result) });
    });
  }, [cancelPending, search]);

  useEffect(() => () => cancelPending(), [cancelPending]);

  // Loading is shown after ~200 ms (03-components §1), not on the first frame of every request.
  useEffect(() => {
    if (phase.kind !== 'loading') {
      setLoadingShown(false);
      return;
    }
    const timer = window.setTimeout(() => setLoadingShown(true), loadingAfterMs);
    return () => window.clearTimeout(timer);
  }, [phase, loadingAfterMs]);

  const items: T[] = phase.kind === 'ready' ? phase.items : [];

  const close = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);

  const settle = useCallback(() => {
    cancelPending();
    setPhase({ kind: 'idle' });
    close();
  }, [cancelPending, close]);

  const choose = (option: T): void => {
    settle();
    onChange(option);
    setText(option.label);
    inputRef.current?.focus();
  };

  const clear = (): void => {
    settle();
    onChange(null);
    setText('');
    inputRef.current?.focus();
  };

  const onInput = (event: ChangeEvent<HTMLInputElement>): void => {
    const next = event.target.value;
    setText(next);
    const query = next.trim();
    // Why abort here and not only at the next request: an answer to the previous query must not land
    // while the field already says something longer.
    cancelPending();
    if (query.length < minLength) {
      setPhase({ kind: 'idle' });
      close();
      return;
    }
    debounce.current = window.setTimeout(() => {
      debounce.current = null;
      run(query);
    }, debounceMs);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (event.nativeEvent.isComposing || composing.current) return;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        const down = event.key === 'ArrowDown';
        if (!open) {
          if (items.length > 0) {
            setOpen(true);
            setActive(down ? 0 : items.length - 1);
          } else if (phase.kind === 'error' || phase.kind === 'denied' || phase.kind === 'ready') {
            setOpen(true);
          } else {
            const query = text.trim();
            if (query.length >= minLength) run(query);
          }
          return;
        }
        if (items.length === 0) return;
        setActive((current) => nextActiveIndex(current, items.length, down));
        return;
      }
      case 'Home':
      case 'End':
        // While an option is active the keys move the option; otherwise the caret, as in any input.
        if (open && active >= 0 && items.length > 0) {
          event.preventDefault();
          setActive(event.key === 'Home' ? 0 : items.length - 1);
        }
        return;
      case 'Enter': {
        if (!open) return;
        // Why: with the list open, Enter is the list's — a form around the field must not submit on it.
        event.preventDefault();
        if (phase.kind === 'error') {
          run(phase.query);
          return;
        }
        const option = items[active];
        if (option) choose(option);
        else close();
        return;
      }
      case 'Escape':
        // Closed, the key is the dialog's; open, it closes the list and restores the held choice's text.
        if (!open) return;
        event.preventDefault();
        event.stopPropagation();
        settle();
        setText(textAfterLeaving(value));
        return;
      case 'Tab':
        // Leaving is not consent: close without choosing; the blur that follows restores the text.
        if (open) settle();
        return;
      default:
        return;
    }
  };

  const onBlur = (): void => {
    settle();
    setText(textAfterLeaving(value));
  };

  // The active option stays in view inside the bounded list.
  useEffect(() => {
    if (!open || active < 0 || typeof document === 'undefined') return;
    document.getElementById(optionId(active))?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  // Anchored beneath the status line under the input (so the count and the capped notice stay visible while the
  // list is open), flipping above the input when the space below is too small, its height bounded by the visual
  // viewport so the active option is never beneath the on-screen keyboard (§3.4).
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const compute = (): void => {
      const input = inputRef.current;
      if (!input) return;
      const rect = input.getBoundingClientRect();
      const status = statusRef.current?.getBoundingClientRect();
      const viewport = window.visualViewport;
      setPosition(popupPlacement({
        input: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width },
        statusBottom: status ? status.bottom : rect.bottom,
        viewTop: viewport ? viewport.offsetTop : 0,
        viewBottom: viewport ? viewport.offsetTop + viewport.height : window.innerHeight,
        innerHeight: window.innerHeight,
      }));
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    window.visualViewport?.addEventListener('resize', compute);
    window.visualViewport?.addEventListener('scroll', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
      window.visualViewport?.removeEventListener('resize', compute);
      window.visualViewport?.removeEventListener('scroll', compute);
    };
  }, [open, items.length, phase.kind]);

  const status = comboboxStatusFor({ phase, loadingShown, value, text, minLength });
  // Between a keystroke and its answer the line keeps the previous answer's count rather than flickering — and never
  // the below-minimum sentence once the text has reached the minimum (`nextStatusLine`).
  const line = nextStatusLine(status, words, carriedStatus.current);
  const statusText = line.text;
  carriedStatus.current = line.carried;

  const describedBy = [error ? errorId : null, hint ? hintId : null, statusId].filter(Boolean).join(' ');
  const activeOption = open && active >= 0 && items[active] ? optionId(active) : undefined;
  const popupStyle: CSSProperties = position
    ? { top: position.top ?? undefined, bottom: position.bottom ?? undefined, left: position.left, inlineSize: position.width, maxBlockSize: position.maxHeight }
    : { top: -9999, left: -9999, visibility: 'hidden' };

  return (
    <div className={['field', 'combobox', error ? 'has-error' : '', className ?? ''].filter(Boolean).join(' ')}>
      <label id={labelId} htmlFor={inputId} className="field__label">
        {label}
        {required ? <span className="field__required" aria-hidden="true"> *</span> : null}
      </label>
      <div className="combobox__control">
        <input
          ref={inputRef}
          id={inputId}
          className="control"
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={listId}
          aria-activedescendant={activeOption}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          autoComplete="off"
          spellCheck={false}
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          onChange={onInput}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onCompositionStart={() => { composing.current = true; }}
          onCompositionEnd={() => { composing.current = false; }}
        />
        {clearable && value ? (
          <IconButton icon="x" label={words.clear} size="sm" className="combobox__clear" onClick={clear} disabled={disabled} />
        ) : null}
        {/* Why pointer-down is prevented: a press on the list must not take focus from the input (§3.4). */}
        <div className="combobox__popup" hidden={!open} style={popupStyle} onPointerDown={(event) => event.preventDefault()}>
          <ul id={listId} role="listbox" aria-labelledby={labelId} className="combobox__list">
            {items.map((option, index) => (
              <li
                key={option.id}
                id={optionId(index)}
                role="option"
                aria-selected={index === active}
                className={['combobox__option', index === active ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => choose(option)}
                onPointerMove={() => { if (active !== index) setActive(index); }}
              >
                <Icon name="check" size={16} className="combobox__option-mark" />
                <span className="combobox__option-text">
                  <span className="combobox__option-label">{option.label}</span>
                  {option.secondary ? <span className="combobox__option-secondary">{option.secondary}</span> : null}
                </span>
              </li>
            ))}
          </ul>
          {phase.kind === 'loading' && loadingShown ? (
            <div className="combobox__notice">
              <span aria-hidden="true"><Spinner size={16} label={words.searching} /></span>
              <span>{words.searching}</span>
            </div>
          ) : null}
          {phase.kind === 'ready' && phase.items.length === 0 ? <div className="combobox__notice">{words.none(phase.query)}</div> : null}
          {phase.kind === 'denied' ? (
            <div className="combobox__notice">
              <Icon name="lock" size={16} />
              <span>{words.denied}</span>
            </div>
          ) : null}
          {phase.kind === 'error' ? (
            <div className="combobox__notice combobox__notice--error">
              <Icon name="alert-circle" size={16} />
              <span>{phase.message}</span>
              <Button size="sm" variant="secondary" onClick={() => run(phase.query)}>{words.retry}</Button>
            </div>
          ) : null}
        </div>
      </div>
      <p id={statusId} ref={statusRef} className="combobox__status" role="status">{statusText}</p>
      {hint ? <p id={hintId} className="field__hint">{hint}</p> : null}
      {error ? <p id={errorId} className="field__error" role="alert">{error}</p> : null}
    </div>
  );
}

// --- The people search (S5; GET /api/people?q=) ---------------------------------------------------

/** The route's cap (`PEOPLE_SEARCH_LIMIT`, src/server/routes/projects.ts): at most twenty people answer. */
export const PEOPLE_SEARCH_LIMIT = 20;

/** Small counts in words, as the below-minimum sentence says them ("two"); beyond nine the figure stands. */
const COUNT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'] as const;

export function countInWords(count: number): string {
  return COUNT_WORDS[count] ?? String(count);
}

export const PEOPLE_WORDS: ComboboxWords = {
  // Why a function: the sentence names the field's own minimum — "two" for the people route's `min(2)`, and a truthful
  // "three" for a picker that asks for three — rather than a figure the words could drift from.
  belowMinimum: (minLength) => `Type ${countInWords(minLength)} or more characters`,
  searching: 'Searching…',
  count: (count) => pluralise(count, { one: '# person', other: '# people' }),
  capped: (count, limit) => `${count} people, showing the first ${limit}`,
  none: (query) => `No one called ‘${query}’`,
  denied: 'You may not search people here',
  failed: 'The search failed',
  chosen: (label) => `${label} chosen`,
  retry: 'Try again',
  clear: 'Clear the chosen person',
};

/** A person as an option: the display name, and the email as secondary text only when the server sent one (two identical names). */
export function peopleOptions(response: PeopleSearchResponse): ComboboxOption[] {
  return response.items.map((person) => (person.email ? { id: person.id, label: person.displayName, secondary: person.email } : { id: person.id, label: person.displayName }));
}

export async function searchPeople(query: string, signal: AbortSignal): Promise<ApiResult<ComboboxSearchResult>> {
  const result = await api.get<PeopleSearchResponse>(`/api/people${queryString({ q: query })}`, { signal });
  if (!result.ok) return result;
  return { ok: true, status: result.status, data: { items: peopleOptions(result.data), limit: PEOPLE_SEARCH_LIMIT } };
}

export type PeopleComboboxProps = Omit<ComboboxProps, 'search' | 'words' | 'minLength'> & {
  /** Overrides of the people words, e.g. a different `clear` name. */
  words?: Partial<ComboboxWords>;
};

/** The combobox over `GET /api/people?q=`: any admitted member by display name, an email only to tell two identical names apart. */
export function PeopleCombobox({ words, ...rest }: PeopleComboboxProps) {
  return <Combobox {...rest} search={searchPeople} words={{ ...PEOPLE_WORDS, ...words }} minLength={2} />;
}
